/**
 * MyProjectsTab
 * Agent's submitted projects with status tracking
 * Shows pending/rejected sales forms at the top for attention
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import Spinner from '../../common/Spinner';
import Button from '../../common/Button';
import ProjectsTable from './shared/ProjectsTable';

export default function MyProjectsTab() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [salesForms, setSalesForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  async function fetchProjects() {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/my-projects');
      // API returns both designProjects and salesForms
      setProjects(response.data.designProjects || []);
      setSalesForms(response.data.salesForms || []);
    } catch (error) {
      console.error('Fetch projects error:', error);
    } finally {
      setLoading(false);
    }
  }

  // Filter sales forms that need attention (pending verification or rejected)
  const formsNeedingAttention = useMemo(() => {
    return salesForms.filter(form =>
      form.status === 'pending_verification' || form.status === 'rejected'
    );
  }, [salesForms]);

  const pendingForms = useMemo(() => {
    return formsNeedingAttention.filter(form => form.status === 'pending_verification');
  }, [formsNeedingAttention]);

  const rejectedForms = useMemo(() => {
    return formsNeedingAttention.filter(form => form.status === 'rejected');
  }, [formsNeedingAttention]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show empty state only if no projects AND no forms needing attention
  if (projects.length === 0 && formsNeedingAttention.length === 0) {
    return (
      <div className="card p-12 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
        <p className="text-gray-600 mb-4">
          Submit your first sales form to see projects here
        </p>
        <Button variant="primary" onClick={() => navigate('/my-leads')}>
          Go to My Leads
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Sales Forms Requiring Attention - Only show if there are pending or rejected forms */}
      {formsNeedingAttention.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-display font-bold">Sales Forms Requiring Attention</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                {formsNeedingAttention.length} {formsNeedingAttention.length === 1 ? 'form' : 'forms'}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {/* Pending Verification Forms */}
            {pendingForms.map((form) => (
              <div key={form.id} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{form.clientName}</p>
                    <p className="text-sm text-gray-600">
                      RM {parseFloat(form.estimatedPackagePrice || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      <span className="mx-2">•</span>
                      Submitted {new Date(form.createdAt).toLocaleDateString('en-MY')}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  Pending Verification
                </span>
              </div>
            ))}

            {/* Rejected Forms */}
            {rejectedForms.map((form) => (
              <div key={form.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{form.clientName}</p>
                    <p className="text-sm text-gray-600">
                      RM {parseFloat(form.estimatedPackagePrice || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      <span className="mx-2">•</span>
                      Submitted {new Date(form.createdAt).toLocaleDateString('en-MY')}
                    </p>
                    {form.rejectionReason && (
                      <p className="text-sm text-red-600 mt-1">
                        <span className="font-medium">Rejection reason:</span> {form.rejectionReason}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/sales-forms/${form.id}/edit`)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Edit & Resubmit
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects Table */}
      {projects.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-display font-bold">My Submitted Projects</h2>
            <span className="text-sm text-gray-600">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </span>
          </div>

          <ProjectsTable
            projects={projects}
            emptyMessage="Submit your first sales form to see projects here"
          />
        </div>
      )}
    </div>
  );
}
